import { expose, transfer } from 'comlink'
import { mradPerSecondToRpmTransform } from './common'
import {
  IMultiMotorTelemetryMessage,
  IRealtimeDataMessage,
  PacketId,
  displayColors
} from './common-types'
import { TimeWindow } from '../hooks/usePlayback'
import { TelemToml, TelemIdxType, TelemValue } from 'cstore/telem'
import { ExportType, exportRecording } from './hid-export'
import {
  SlntHeader,
  VariableTelemSamples,
  MultiMotorTelemSamples,
  SubActuators,
  TelemSetElement
} from './slnt-file'
import { plotKeyToRgbStr } from './plot-key-colors'
import { multiMotorFieldTypes } from '../connections/pscb'

export type PlotKey = string // Pre-variable telemetry, was: keyof IRealtimeDataMessage;

// This info is encoded in PlotKey for telemetry from sub-actuators (e.g., as used by PSCB)
export type MultiMotorInfo = {
  plotKey: string
  baseName: string
  label: string // Actuator disposition, typically
}

export type RealTimeDataPacket = {
  id: PacketId.REALTIME_DATA
  packet: IRealtimeDataMessage
  raw?: DataView
  timestampMs: number
}

let _realTimeDataPackets: RealTimeDataPacket[] = []

// Intentionally short names because it'll get stored as JSON; ts is timestamp in milliseconds and val is the telemetry value
export type TelemetryItem = {
  ts: number
  val: number | boolean
}
const telemArrays: Map<PlotKey, TelemetryItem[]> = new Map()

// Storage for multi-motor telemetry: "MultiMotor_N${nodeId}_${fieldName}" -> TelemetryItem[]
const multiMotorTelemArrays: Map<PlotKey, TelemetryItem[]> = new Map()

export type TimeSeriesItem = RealTimeDataPacket | TelemetryItem

// Value range for each different telemetry stream: [ low, high ]
export type TimeSeriesRanges = { [fieldName: string]: [number, number] }

const defaultTimeSeriesRanges = (): TimeSeriesRanges => {
  return { motorSpeed: [-10, 10] }
}

let _timeSeriesRanges: TimeSeriesRanges = defaultTimeSeriesRanges()

export const getTimestamp = (item: TimeSeriesItem): number => {
  return 'ts' in item ? item.ts : item.timestampMs
}

// TODO: use a better approach for getting these (i.e., generate this array using the Python code in salient-types)
const keysOfIRealtimeDataMessage: PlotKey[] = [
  'motorSpeed',
  'motorCurrent',
  'batteryVoltage',
  'batteryCurrent',
  'servoPulse',
  'inverterTemp',
  'controllerState',
  'faultCodeDeprecated',
  'uptimeMs',
  'motorPosition',
  'positionSensorReading',
  'faultsBitmapLower32',
  'faultsBitmapUpper16',
  'externalThermTemp',
  'rawPositionSensor',
  'sysidFrequency',
  'sysidMagnitude'
]

const timeSeriesForPlotKey = (plotKey: PlotKey): TimeSeriesItem[] | undefined => {
  if (keysOfIRealtimeDataMessage.includes(plotKey)) {
    return _realTimeDataPackets
  }
  return telemArrays.get(plotKey) ?? multiMotorTelemArrays.get(plotKey)
}

// Populated via setActuatorDispositions. Cleared on device disconnect.
let _subActuators: SubActuators = {}

// This returns a prefix string, to which a field in
// IMultiMotorTelemetryMessage should be appended. Note that all fields but one
// (actuatorNodeId) from said type should be used with the returned prefix.
export const multiMotorPrefixFromNodeId = (actuatorNodeId: number): string => {
  return `MultiMotor_N${actuatorNodeId}_`
}

export const splitMultiMotorPlotKey = (
  plotKey: PlotKey
): { nodeId: number; baseName: string } | undefined => {
  const match = plotKey.match(/^MultiMotor_N(\d+)_(.+)$/)
  if (!match) return undefined
  const nodeId = Number(match[1])
  const baseName = match[2]
  return { nodeId, baseName }
}

// Caller must ensure plotKey is valid.
const getValue = (item: TimeSeriesItem, plotKey: PlotKey): TelemValue => {
  return 'val' in item ? item.val : item.packet[plotKey as keyof IRealtimeDataMessage]
}

// Caller must ensure plotKey is valid.
// Caller must ensure not called on a boolean time series
const getNumericValue = (item: TimeSeriesItem, plotKey: PlotKey): number => {
  return getValue(item, plotKey) as number
}

const firstIndexAtOrAfterTimestamp = (items: TimeSeriesItem[], targetMs: number): number | null => {
  if (items.length === 0) {
    return null
  }

  let startIndex = 0
  let endIndex = items.length - 1
  let centerIndex: number, curTimestamp: number

  while (true) {
    centerIndex = Math.floor((startIndex + endIndex) / 2)
    curTimestamp = getTimestamp(items[centerIndex])

    if (centerIndex === endIndex) {
      return curTimestamp >= targetMs ? centerIndex : null
    }

    if (targetMs > curTimestamp) {
      startIndex = centerIndex + 1

      if (getTimestamp(items[startIndex]) >= targetMs) {
        return startIndex
      }
    } else {
      endIndex = centerIndex
    }
  }
}

const lastIndexAtOrBeforeTimestamp = (items: TimeSeriesItem[], targetMs: number): number | null => {
  if (items.length === 0) {
    return null
  }

  let startIndex = 0
  let endIndex = items.length - 1
  let centerIndex: number, curTimestamp: number

  while (true) {
    centerIndex = Math.ceil((startIndex + endIndex) / 2)
    curTimestamp = getTimestamp(items[centerIndex])

    if (startIndex === endIndex) {
      return curTimestamp <= targetMs ? centerIndex : null
    }

    if (curTimestamp > targetMs) {
      endIndex = centerIndex - 1

      if (getTimestamp(items[endIndex]) <= targetMs) {
        return endIndex
      }
    } else {
      startIndex = centerIndex
    }
  }
}

// Caller must ensure plotKey specifies a valid numeric time series.
const renderPlot = (
  items: TimeSeriesItem[],
  plotKey: PlotKey,
  startIndex: number,
  endIndex: number,
  timeWindow: TimeWindow,
  dx: number,
  dy: number,
  sx: number,
  sy: number,
  canvasContext: OffscreenCanvasRenderingContext2D
) => {
  let _yRange: [number, number] | undefined
  _yRange = _timeSeriesRanges[plotKey]
  canvasContext.strokeStyle = displayColor(plotKey)

  if (_yRange === undefined) {
    return null
  }

  const yRange = _yRange

  interface IExtremum {
    timestampMs: number
    value: number
  }

  let isFirstPoint = true

  const plotExtremum = (e: IExtremum) => {
    const xPos = ((e.timestampMs - timeWindow[0]) / (timeWindow[1] - timeWindow[0])) * sx
    const yPos = ((yRange[1] - e.value) / (yRange[1] - yRange[0])) * sy
    if (isFirstPoint) {
      canvasContext.moveTo(dx + 0, dy + yPos)
      isFirstPoint = false
    }
    canvasContext.lineTo(dx + xPos, dy + yPos)
  }

  const sliceLength = endIndex - startIndex

  if (sliceLength === 0) {
    return null
  }

  canvasContext.beginPath()

  const downsampleSize = sx * 1.2
  const divisor = Math.ceil((sliceLength / downsampleSize) * 2)

  let high: IExtremum | undefined
  let low: IExtremum | undefined

  for (let i = startIndex; i <= endIndex; i++) {
    const value = getNumericValue(items[i], plotKey)

    if (high === undefined) {
      high = { timestampMs: getTimestamp(items[i]), value }
    } else if (value > high.value) {
      if (low === undefined) {
        low = high
      }
      high = { timestampMs: getTimestamp(items[i]), value }
    } else if (low === undefined || value < low.value) {
      low = { timestampMs: getTimestamp(items[i]), value }
    }

    if (i % divisor === 0) {
      if (high === undefined) {
        high = low = undefined
        continue
      }

      if (low === undefined) {
        plotExtremum(high)
        high = low = undefined
        continue
      }

      if (high.timestampMs < low.timestampMs) {
        plotExtremum(high)
        plotExtremum(low)
      } else {
        plotExtremum(low)
        plotExtremum(high)
      }

      high = low = undefined
    }
  }

  canvasContext.stroke()
}

interface ITick {
  yPosition: number
  color: string
  value: number
}

export const displayColor = (plotKey: PlotKey): string => {
  const realTimeDataDisplayColors = displayColors[PacketId.REALTIME_DATA]
  if (plotKey in realTimeDataDisplayColors) {
    return realTimeDataDisplayColors[plotKey as keyof IRealtimeDataMessage]
  }
  return plotKeyToRgbStr(plotKey)
}

const getTicks = (plotKeys: PlotKey[], sy: number) => {
  const packetTicks: Map<PlotKey, ITick[]> = new Map()

  for (const plotKey of plotKeys) {
    const yRange: [number, number] | undefined = _timeSeriesRanges[plotKey]
    const color: string = displayColor(plotKey)

    if (yRange === undefined) {
      continue
    }

    const ticks: ITick[] = []

    const ySpread = yRange[1] - yRange[0]
    const orderOfMag = Math.round(Math.log10(ySpread))
    let distanceBetweenLines = 10 ** (orderOfMag - 1)

    const numLines = ySpread / distanceBetweenLines
    distanceBetweenLines *= Math.ceil(Math.max(numLines / (sy / 50), 1))

    const firstTickValue = Math.ceil(yRange[0] / distanceBetweenLines) * distanceBetweenLines
    for (let tick = firstTickValue; tick <= yRange[1]; tick += distanceBetweenLines) {
      if (tick === 0) {
        continue
      }

      const yPos = ((yRange[1] - tick) / ySpread) * sy
      ticks.push({
        yPosition: yPos,
        color,
        value: tick
      })
    }

    if (yRange[0] <= 0 && yRange[1] >= 0) {
      const yPos = (yRange[1] / ySpread) * sy
      ticks.push({
        yPosition: yPos,
        color,
        value: 0
      })
    }

    packetTicks.set(plotKey, ticks)
  }

  return packetTicks
}

const renderAxisLines = (
  ticks: ITick[],
  dx: number,
  dy: number,
  sx: number,
  lineWidth: number,
  canvasContext: OffscreenCanvasRenderingContext2D
) => {
  canvasContext.lineWidth = lineWidth
  for (const tick of ticks) {
    canvasContext.strokeStyle = `${tick.color}33`
    if (tick.value === 0) {
      canvasContext.strokeStyle = '#000000aa'
    }
    canvasContext.beginPath()
    canvasContext.moveTo(dx, dy + tick.yPosition)
    canvasContext.lineTo(dx + sx, dy + tick.yPosition)
    canvasContext.stroke()
  }
}

const renderAxisLabels = (
  ticks: ITick[],
  dx: number,
  dy: number,
  sy: number,
  fontSize: number,
  padding: number,
  canvasContext: OffscreenCanvasRenderingContext2D
) => {
  const labelHeight = fontSize + padding * 2
  canvasContext.textAlign = 'left'
  canvasContext.font = `${fontSize}px monospace`
  let maxWidth: number = 0

  for (const tick of ticks) {
    let y = dy + tick.yPosition - labelHeight / 2
    y = Math.max(0, y)
    y = Math.min(dy + sy - labelHeight - 2, y)
    const text = Number.isInteger(tick.value) ? tick.value.toString() : tick.value.toFixed(2)
    const { width: textWidth } = canvasContext.measureText(text)
    const labelWidth = textWidth + padding * 2
    canvasContext.fillStyle = tick.color
    canvasContext.fillRect(dx, y, labelWidth + 4, labelHeight + 4)
    canvasContext.fillStyle = '#000000'
    canvasContext.fillRect(dx + 2, y + 2, labelWidth, labelHeight)
    canvasContext.fillStyle = '#ffffff'
    canvasContext.fillText(text, dx + padding + 2, y + fontSize + 2 + 2)

    maxWidth = Math.max(maxWidth, labelWidth + 4)
  }

  return maxWidth
}

const renderXAxis = (
  timeWindow: TimeWindow,
  dx: number,
  dy: number,
  sx: number,
  sy: number,
  xHover: number | undefined,
  canvasContext: OffscreenCanvasRenderingContext2D
) => {
  const startSec = timeWindow[0] / 1e3
  const endSec = timeWindow[1] / 1e3
  const xSpread = endSec - startSec

  const orderOfMag = Math.round(Math.log10(xSpread))
  let distanceBetweenLines = 10 ** (orderOfMag - 1)

  const numLines = xSpread / distanceBetweenLines
  distanceBetweenLines *= Math.ceil(Math.max(numLines / 8, 1))

  canvasContext.lineWidth = 1
  canvasContext.font = `${sy - 4}px monospace`

  const hoverTick =
    xHover === undefined
      ? undefined
      : (xHover * (timeWindow[1] - timeWindow[0]) + timeWindow[0]) / 1e3

  const firstTickValue = Math.ceil(startSec / distanceBetweenLines) * distanceBetweenLines
  for (let tick = firstTickValue; tick <= endSec; tick += distanceBetweenLines) {
    if (
      hoverTick !== undefined &&
      (Math.abs(hoverTick - tick) < distanceBetweenLines ||
        Math.abs(tick - hoverTick) < distanceBetweenLines)
    ) {
      continue
    }
    let text = Number.isInteger(tick) ? tick.toString() : tick.toFixed(2)
    text += 's'
    const { width: textWidth } = canvasContext.measureText(text)
    const xPos = ((tick - startSec) / xSpread) * sx
    canvasContext.fillStyle = '#ffffff'
    canvasContext.fillText(text, dx + xPos + 4 - textWidth / 2, dy + (sy - 4) + 1)
  }

  if (hoverTick !== undefined) {
    let text = hoverTick.toFixed(3)
    text += 's'
    const { width: textWidth } = canvasContext.measureText(text)
    const xPos = ((hoverTick - startSec) / xSpread) * sx
    canvasContext.fillStyle = '#ffffff'
    canvasContext.fillText(text, dx + xPos + 4 - textWidth / 2, dy + (sy - 4) + 1)
  }
}

const renderHoverLine = (
  xHoverValue: number,
  dx: number,
  dy: number,
  sx: number,
  sy: number,
  canvasContext: OffscreenCanvasRenderingContext2D
) => {
  canvasContext.strokeStyle = '#ffffff'
  canvasContext.lineWidth = 1
  const xPos = dx + xHoverValue * sx
  canvasContext.beginPath()
  canvasContext.moveTo(xPos, dy + sy)
  canvasContext.lineTo(xPos, dy)
  canvasContext.stroke()
}

// Update _timeSeriesRanges for legacy telemetry packets.
const updateRangesForRealTimeDataPackets = (packet: RealTimeDataPacket) => {
  for (const plotKey of Object.keys(packet.packet)) {
    updateRange(packet, plotKey)
  }
}

// Update _timeSeriesRanges (lower and upper Y values for this plot / time series)
// This is a no-op for boolean time series.
const updateRange = (item: TimeSeriesItem, plotKey: PlotKey) => {
  let value = getValue(item, plotKey)
  if (typeof value !== 'number') {
    return
  }
  let range: [number, number] | undefined = _timeSeriesRanges[plotKey]
  if (range === undefined) {
    range = [value * 0.9 - 5e-2, value * 1.1 + 5e-2]
  } else if (value > range[1]) {
    range[1] = value
  } else if (value < range[0]) {
    range[0] = value
  }

  _timeSeriesRanges[plotKey] = range
}

const telemTypeSize = (fieldType: TelemIdxType): number => {
  switch (fieldType) {
    case 'TYPE_BOOL':
      return 1
    case 'TYPE_FLOAT':
      return 4
    case 'TYPE_INT32':
      return 4
    case 'TYPE_UINT32':
      return 4
    case 'TYPE_UINT16':
      return 2
  }
}

// Note: currently there is no way to do compile-time assertions that this size
// is indeed 59 bytes; support would probably have to be added salient-types'
// TS generator.
const TELEM_BYTESTREAM_SIZE = 59 // bytes

type DeserializeFn = (dv: DataView, byteOffset: number) => TelemValue

// The keys are `telemId` integers which are extracted from the
// VARIABLE_TELEMETRY packet payloads.
// The values are 3-element arrays specifying how to extract the telemetry
// values from their corresponding packets, and also what field name to call
// those values by.
type TelemMap = { [telemId: number]: [fieldName: string, byteOffset: number, fn: DeserializeFn][] }

let telemMap: TelemMap = {}

const deserFns: { [fieldType: string]: DeserializeFn } = {
  TYPE_BOOL: (dv: DataView, byteOffset: number) => !!dv.getInt8(byteOffset),
  TYPE_FLOAT: (dv: DataView, byteOffset: number) => dv.getFloat32(byteOffset, true),
  TYPE_INT32: (dv: DataView, byteOffset: number) => dv.getInt32(byteOffset, true),
  TYPE_UINT32: (dv: DataView, byteOffset: number) => dv.getUint32(byteOffset, true),
  TYPE_UINT16: (dv: DataView, byteOffset: number) => dv.getUint16(byteOffset, true)
}

// buildTelemMap takes the telemSet (supplied by loadTelemMap caller) and breaks it down into:
//   telemId => [ [ fieldName, byteOffset, deserializeFn ], ... ]
const buildTelemMap = (telemSet: TelemSetElement[]): TelemMap => {
  let telemMap: TelemMap = {}
  let offset = 0
  let telemId = 1

  for (const { fieldName, fieldType } of telemSet) {
    const deserializeFn = deserFns[fieldType]
    const size = telemTypeSize(fieldType)
    if (offset + size > TELEM_BYTESTREAM_SIZE) {
      offset = 0
      telemId++
    }
    telemMap[telemId] ??= []
    telemMap[telemId].push([fieldName, offset, deserializeFn])
    offset += size
  }

  return telemMap
}

let _telemSet: TelemSetElement[] = []
let _multiMotorTelemSet: TelemSetElement[] = []
let _playbackSlntHeader: SlntHeader | undefined

// Must be initialized on startup; this works around a Rollup issue with ?raw loading in web workers
let _telemToml: TelemToml | undefined

export interface IPacketWorkerApi {
  handlePackets: (packets: RealTimeDataPacket[]) => void
  loadTelemHeader: (header: SlntHeader) => void
  loadTelemMap: (telemSet: TelemSetElement[]) => void
  loadMultiMotorTelemSet: (multiMotorTelemSet: TelemSetElement[]) => void
  loadTelemToml: (telemToml: TelemToml) => void
  handleVariableTelemetry: (
    timestampMs: number,
    telemId: number,
    telemBytestream: Uint8Array
  ) => void
  handleVarTelemPlayback: (vtss: VariableTelemSamples[]) => void
  handleMultiMotorTelemPlayback: (mmtss: MultiMotorTelemSamples[]) => void
  handleMultiMotorTelemetry: (timestampMs: number, packet: IMultiMotorTelemetryMessage) => void
  setActuatorDispositions: (subActuators: SubActuators) => void
  renderBitmap: (
    plotKeys: PlotKey[],
    timeWindow: TimeWindow,
    width: number,
    height: number,
    xHoverValue: number | undefined
  ) => [ImageBitmap | null, Map<string, number> | null]
  getLatestTelemValueNotAfter: (fieldName: PlotKey, timestampMs: number) => TelemValue | null
  export: (
    filename: string,
    exportType: ExportType,
    timeWindow: TimeWindow,
    downloadFn: (filename: string, file: string) => void
  ) => void
  clear: () => void
}

export const api: IPacketWorkerApi = {
  handlePackets: (packets: RealTimeDataPacket[]) => {
    for (const packet of packets) {
      updateRangesForRealTimeDataPackets(packet)
      _realTimeDataPackets.push(packet)
    }
  },
  loadTelemHeader: (header: SlntHeader) => {
    _playbackSlntHeader = header
  },
  loadTelemMap: (telemSet: TelemSetElement[]) => {
    _telemSet = telemSet
    telemMap = buildTelemMap(telemSet)
  },
  loadMultiMotorTelemSet: (multiMotorTelemSet: TelemSetElement[]) => {
    _multiMotorTelemSet = multiMotorTelemSet
  },
  // Must be called on startup
  loadTelemToml: (telemToml: TelemToml) => {
    _telemToml = telemToml
  },
  handleVariableTelemetry: (timestampMs: number, telemId: number, telemBytestream: Uint8Array) => {
    const dv = new DataView(
      telemBytestream.buffer,
      telemBytestream.byteOffset,
      telemBytestream.byteLength
    )
    const arr = telemMap[telemId]
    if (!arr || !arr.length) return
    for (const [fieldName, byteOffset, deserializeFn] of arr) {
      const val = deserializeFn(dv, byteOffset)
      const telemItem: TelemetryItem = {
        ts: timestampMs,
        val
      }
      let telemArray = telemArrays.get(fieldName)
      if (!telemArray) {
        telemArray = []
        telemArrays.set(fieldName, telemArray)
      }
      telemArray.push(telemItem)
      updateRange(telemItem, fieldName)
    }
  },
  // This receives one or more packets of expanded telemetry data and adds
  // the data in the packet(s) (via pushing to the appropriate telemetry
  // array and updating ranges, like handleVariableTelemetry does). Unlike
  // handleVariableTelemetry, this is for playback from an SLNT file.
  handleVarTelemPlayback: (vtss: VariableTelemSamples[]) => {
    for (let i = 0; i < vtss.length; i++) {
      const { ts, o: offset, a: vtsValuesArray } = vtss[i]
      // vtsValuesArray[0] corresponds to _telemSet[offset]
      const limit = Math.min(vtsValuesArray.length, _telemSet.length - offset)
      for (let j = 0; j < limit; j++) {
        const { fieldName } = _telemSet[offset + j]
        let telemArray = telemArrays.get(fieldName)
        if (!telemArray) {
          telemArray = []
          telemArrays.set(fieldName, telemArray)
        }
        const telemItem = {
          ts,
          val: vtsValuesArray[j]
        }
        telemArray.push(telemItem)
        updateRange(telemItem, fieldName)
      }
    }
  },
  handleMultiMotorTelemPlayback: (mmtss: MultiMotorTelemSamples[]) => {
    for (let i = 0; i < mmtss.length; i++) {
      const { ts, o: offset, a: valuesArray } = mmtss[i]
      // valuesArray[0] corresponds to _multiMotorTelemSet[offset]
      const limit = Math.min(valuesArray.length, _multiMotorTelemSet.length - offset)
      for (let j = 0; j < limit; j++) {
        const { fieldName } = _multiMotorTelemSet[offset + j]
        let telemArray = multiMotorTelemArrays.get(fieldName)
        if (!telemArray) {
          telemArray = []
          multiMotorTelemArrays.set(fieldName, telemArray)
        }
        const telemItem = {
          ts,
          val: valuesArray[j]
        }
        telemArray.push(telemItem)
        updateRange(telemItem, fieldName)
      }
    }
  },
  handleMultiMotorTelemetry: (timestampMs: number, packet: IMultiMotorTelemetryMessage) => {
    const { actuatorNodeId } = packet
    const prefix = multiMotorPrefixFromNodeId(actuatorNodeId)

    const fields: [string, number][] = [
      ['faultsBitmapLower32', packet.faultsBitmapLower32],
      ['faultsBitmapUpper16', packet.faultsBitmapUpper16],
      // FW sends this as a fixed-point int32 in units of 0.01% (10000 = 100.00%)
      // to carry 2 decimal digits of precision over the wire; divide back down
      // to an actual percentage before it enters the telemetry pipeline.
      ['positionActualPctOfRange', packet.positionActualPctOfRange / 100],
      ['velocityActualValue', packet.velocityActualValue],
      ['torqueActualValue', packet.torqueActualValue],
      ['electricalCurrentActualValue', packet.electricalCurrentActualValue],
      ['targetPositionPctOfRange', packet.targetPositionPctOfRange / 100],
      ['statusWord', packet.statusWord],
      ['mosfetTemperature', packet.mosfetTemperature],
      ['batteryCurrent', packet.batteryCurrent],
      ['motorCurrent', packet.motorCurrent],
      ['motorTemperature', packet.motorTemperature],
      ['positionActualValue', packet.positionActualValue],
      ['positionSensorRawValue', packet.positionSensorRawValue],
      ['positionUserUnits', packet.positionUserUnits],
      // the following packetKeys are derived, which allow for easy access to different unit interpretations
      // without clogging up the packet pipeline
      ['positionActualValueRpm', mradPerSecondToRpmTransform(packet.positionActualValue)]
    ]

    for (const [fieldName, val] of fields) {
      const plotKey = prefix + fieldName
      const telemItem: TelemetryItem = { ts: timestampMs, val }
      let telemArray = multiMotorTelemArrays.get(plotKey)
      if (!telemArray) {
        telemArray = []
        multiMotorTelemArrays.set(plotKey, telemArray)
      }
      telemArray.push(telemItem)
      updateRange(telemItem, plotKey)
    }
  },
  setActuatorDispositions: (subActuators: SubActuators) => {
    _subActuators = subActuators
  },
  renderBitmap: (plotKeys, timeWindow, width, height, xHoverValue) => {
    const canvas = new OffscreenCanvas(width, height)
    const canvasContext = canvas.getContext('2d') as OffscreenCanvasRenderingContext2D | null
    if (canvasContext === null) {
      return [null, null]
    }

    const fontSize = 14
    const padding = 4

    const xAxisDx = 0
    const xAxisHeight = 18
    const xAxisDy = height - xAxisHeight - 1
    const xAxisWidth = width

    const plotDx = 0
    const plotDy = 0
    const plotHeight = height - xAxisHeight - 1
    const plotWidth = width

    renderXAxis(timeWindow, xAxisDx, xAxisDy, xAxisWidth, xAxisHeight, xHoverValue, canvasContext)

    const packetTicks = getTicks(plotKeys, plotHeight)
    for (const [, ticks] of packetTicks) {
      renderAxisLines(ticks, plotDx, plotDy, plotWidth, 1, canvasContext)
    }

    canvasContext.lineWidth = 2

    let realTimeDataStartIndex = firstIndexAtOrAfterTimestamp(_realTimeDataPackets, timeWindow[0])
    let realTimeDataEndIndex = lastIndexAtOrBeforeTimestamp(_realTimeDataPackets, timeWindow[1])

    let isAnyTimeSeriesAvailable = false
    for (const plotKey of plotKeys) {
      const timeSeries = timeSeriesForPlotKey(plotKey)
      if (!timeSeries) continue

      let startIndex = null
      let endIndex = null
      if (timeSeries === _realTimeDataPackets) {
        startIndex = realTimeDataStartIndex
        endIndex = realTimeDataEndIndex
      } else {
        startIndex = firstIndexAtOrAfterTimestamp(timeSeries, timeWindow[0])
        endIndex = lastIndexAtOrBeforeTimestamp(timeSeries, timeWindow[1])
      }
      if (startIndex === null || endIndex === null) return [null, null]
      isAnyTimeSeriesAvailable = true

      renderPlot(
        timeSeries,
        plotKey,
        startIndex,
        endIndex,
        timeWindow,
        plotDx,
        plotDy,
        plotWidth,
        plotHeight,
        canvasContext
      )
    }
    if (!isAnyTimeSeriesAvailable) return [null, null]

    let hoverValues: Map<string, number> | null = null
    if (xHoverValue !== undefined) {
      renderHoverLine(xHoverValue, plotDx, plotDy, plotWidth, plotHeight, canvasContext)
      hoverValues = new Map()
      for (const plotKey of plotKeys) {
        const timeSeries = timeSeriesForPlotKey(plotKey)
        if (!timeSeries) return [null, null]

        const hoverPacketIndex = lastIndexAtOrBeforeTimestamp(
          timeSeries,
          xHoverValue * (timeWindow[1] - timeWindow[0]) + timeWindow[0]
        )
        if (hoverPacketIndex === null) continue

        hoverValues.set(plotKey, getNumericValue(timeSeries[hoverPacketIndex], plotKey))
      }
    }

    let xOffset = 0
    for (const [, ticks] of packetTicks) {
      xOffset += renderAxisLabels(
        ticks,
        plotDx + xOffset,
        plotDy,
        plotHeight,
        fontSize,
        padding,
        canvasContext
      )
    }

    let bmp
    try {
      bmp = canvas.transferToImageBitmap()
    } catch (e) {
      console.warn('Failed to render plot bitmap in packet worker', e)
      return [null, null]
    }
    return transfer([bmp, hoverValues], [bmp])
  },
  getLatestTelemValueNotAfter: (fieldName: PlotKey, timestampMs: number): TelemValue | null => {
    const timeSeries = timeSeriesForPlotKey(fieldName)
    if (!timeSeries) return null

    // Optimization -- often this function is called for the present
    // timestamp (live streaming).
    let item = timeSeries[timeSeries.length - 1]
    if (!item) return null
    if (getTimestamp(item) <= timestampMs) return getValue(item, fieldName)

    // Slower case -- binary search
    const lastIndex = lastIndexAtOrBeforeTimestamp(timeSeries, timestampMs)
    if (lastIndex === null) return null
    item = timeSeries[lastIndex]
    if (!item) return null
    return getValue(item, fieldName)
  },
  export: (filename, exportType, timeWindow, downloadFn) => {
    let packets = _realTimeDataPackets
    // ToDo: this assumes we still capture the legacy REALTIME_DATA packets.
    const startIndex = firstIndexAtOrAfterTimestamp(packets, timeWindow[0])
    const endIndex = lastIndexAtOrBeforeTimestamp(packets, timeWindow[1])
    if (startIndex === null || endIndex === null) {
      return
    }
    packets = _realTimeDataPackets.slice(startIndex, endIndex)

    // Make telemInfo from _telemSet and units in telem TOML file
    const telemInfo = _telemSet.map((el) => {
      const { fieldName } = el
      // Get units from TOML file
      let units = ''
      units = _playbackSlntHeader?.fieldsMetadata[fieldName]?.units ?? ''
      if (!units) {
        for (const entry of _telemToml!.telemetry.definitions) {
          if (entry.name === fieldName) {
            units = entry.units ?? ''
            break
          }
        }
      }
      return { ...el, units }
    })
    // Build multiMotorTelemSet from the keys of multiMotorTelemArrays (sorted),
    // stripping the "MultiMotor_N{n}_" prefix and looking up the type from multiMotorFieldTypes.
    const mmPlotKeys = Array.from(multiMotorTelemArrays.keys()).sort()
    const exportMultiMotorTelemSet = mmPlotKeys.flatMap((plotKey) => {
      const match = plotKey.match(/^MultiMotor_N\d+_(.+)$/)
      if (!match) return []
      const suffix = match[1]
      const fieldType = multiMotorFieldTypes[suffix]
      if (!fieldType) return []
      return [{ fieldName: plotKey, fieldType }]
    })
    const file = exportRecording(
      packets,
      telemArrays,
      filename,
      exportType,
      telemInfo,
      multiMotorTelemArrays,
      exportMultiMotorTelemSet,
      _subActuators
    )
    if (file === undefined) {
      return
    }
    downloadFn(file[0], file[1])
  },
  clear: () => {
    _timeSeriesRanges = defaultTimeSeriesRanges()
    _playbackSlntHeader = undefined
    _telemSet = []
    _multiMotorTelemSet = []
    _realTimeDataPackets = []
    telemArrays.clear()
    multiMotorTelemArrays.clear()
    telemMap = {}
    _subActuators = {}
  }
}

expose(api)
export default {} as typeof Worker & { new (): Worker }
